import { Controller } from "@hotwired/stimulus"
import $ from "jquery"
import DataTable from "datatables.net-bs5"
import "datatables.net-bs5/css/dataTables.bootstrap5.css"

// Official DataTables French translation, embedded directly (no external
// fetch — consistent with the rest of the project, which never loads
// anything from a CDN at runtime). English falls back to the library's
// own built-in default when the page locale isn't "fr".
const FR_TRANSLATION = {
  emptyTable: "Aucune donnée disponible",
  info: "Affichage de _START_ à _END_ sur _TOTAL_ entrées",
  infoEmpty: "Affichage de 0 à 0 sur 0 entrée",
  infoFiltered: "(filtré à partir de _MAX_ entrées au total)",
  lengthMenu: "Afficher _MENU_ entrées",
  loadingRecords: "Chargement...",
  processing: "Traitement...",
  search: "Rechercher :",
  zeroRecords: "Aucun résultat trouvé",
  paginate: { first: "Premier", last: "Dernier", next: "Suivant", previous: "Précédent" },
  aria: { sortAscending: ": activer pour trier la colonne par ordre croissant", sortDescending: ": activer pour trier la colonne par ordre décroissant" }
}

// Wraps a plain <table> with DataTables for client-side sort/search/paging.
// Usage: data-controller="datatable" on the <table> (needs a <thead> with
// one <th> per column). Optional data-datatable-page-length-value="25" and
// data-datatable-order-value='[[0, "desc"]]' (column index + direction).
//
// This controller looks more defensive than a typical Stimulus controller
// for a good reason: DataTables restructures its target's DOM at init time
// (wraps the <table>, injects a search box, pagination, length menu).
// Stimulus's own MutationObserver perceives that restructuring as
// "elements matching data-controller appeared/disappeared" and fires
// connect()/disconnect() again on the same underlying table — which,
// without the two guards below, caused a genuine, repeatedly-reproduced
// infinite construct/destroy loop that froze the page (confirmed via
// hundreds of instrumented runs; several other approaches — a plain init
// flag, DataTables' own retrieve option alone, stripping the
// data-controller attribute — did not reliably stop it).
export default class extends Controller {
  static values = {
    pageLength: { type: Number, default: 15 },
    order: { type: Array, default: [] },
    // Some admin tables have a trailing "actions" column (edit/delete
    // buttons) that shouldn't be sortable or included in the search index.
    noSortLast: { type: Boolean, default: true }
  }

  connect() {
    // Guard 1: if this element is already a live DataTable (a spurious
    // reconnect caused by the restructuring below, not a genuine new
    // table), do nothing.
    if (DataTable.isDataTable(this.element)) return

    const columnCount = this.element.querySelectorAll("thead th").length
    const columnDefs = this.noSortLastValue && columnCount > 0
      ? [{ targets: columnCount - 1, orderable: false, searchable: false }]
      : []
    const locale = document.documentElement.lang

    // Detach the table before constructing DataTables, so its internal DOM
    // restructuring happens on a node with no MutationObserver watching it
    // (detached nodes aren't part of the observed tree), then reattach the
    // fully-built result in one operation once DataTables is completely
    // finished — rather than live, visibly, piece by piece.
    const placeholder = document.createComment("datatable-placeholder")
    this.element.replaceWith(placeholder)

    this.table = new DataTable(this.element, {
      pageLength: this.pageLengthValue,
      // Must include pageLengthValue itself, or the "entries per page"
      // dropdown has no option matching the table's actual current page
      // size — the <select> then shows blank (no option marked selected)
      // even though it lists real, working choices. Confirmed: with the
      // default lengthMenu ([10, 25, 50, 100]) and pageLength: 15, the
      // rendered <select>'s value was "" despite having options.
      lengthMenu: [...new Set([10, this.pageLengthValue, 25, 50, 100])].sort((a, b) => a - b),
      order: this.orderValue.length ? this.orderValue : [],
      columnDefs,
      language: locale === "fr" ? FR_TRANSLATION : undefined,
      autoWidth: false,
      retrieve: true
    })

    // DataTables wraps the original <table> in its own container div(s);
    // `table().container()` returns that outermost wrapper via the
    // DataTables API, which is what needs to go back where the table used
    // to live.
    const wrapper = this.table.table().container()
    placeholder.replaceWith(wrapper)
  }

  disconnect() {
    // Guard 2: only tear down if the element has genuinely left the
    // document — not on every disconnect() call. The detach/reattach
    // dance in connect() above causes Stimulus to fire disconnect() too,
    // even though the element is just being moved/restructured, not
    // actually removed. Destroying unconditionally there tore down the
    // very DataTables registration that guard 1 and retrieve: true depend
    // on, which was the actual cause of the infinite loop (confirmed:
    // removing this document.contains() check reproduces the freeze
    // again, keeping it stops the freeze reliably across repeated runs).
    if (!document.contains(this.element)) {
      this.table?.destroy()
    }
  }
}
